import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect, Match, Schema } from "effect";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { StaffDialog } from "#order-dialog.tsx";
import { KitchenPresetSettings } from "#kitchen-preset-settings.tsx";
import { StaffAccountSettings } from "#staff-account-settings.tsx";
import { PaymentDeviceSettings } from "#payment-devices.tsx";
import { useLanguage } from "#i18n.tsx";
import type { StaffLanguage } from "../../../packages/common/src/schemas/auth.ts";

const connectionSchema = Schema.Struct({
  connected: Schema.Boolean,
  merchantId: Schema.optional(Schema.String),
  environment: Schema.optional(Schema.String),
});
const storeSchema = Schema.Struct({
  status: Schema.Literal("open", "closed"),
});
const authorizeSchema = Schema.Struct({
  url: Schema.String.pipe(
    Schema.filter((value) => {
      try {
        new URL(value);
        return true;
      } catch {
        return "Expected a URL";
      }
    }),
  ),
  state: Schema.String.pipe(Schema.minLength(1)),
});
const successSchema = Schema.Struct({
  success: Schema.Literal(true),
  message: Schema.optional(Schema.String),
});
type SquareIntegration = {
  readonly connection: typeof connectionSchema.Type;
  readonly store: typeof storeSchema.Type | undefined;
};
type OAuthCallback = {
  readonly code: string;
  readonly state: string;
};
type StoreChange = {
  readonly merchantId: string;
  readonly status: "open" | "closed";
};

/** Existing store controls and Square OAuth management, using the shared staff session. */
export function SettingsPage() {
  const { api } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [disconnecting, setDisconnecting] = useState(false);
  const callbackStarted = useRef(false);
  const integrationQueryKey = ["settings", "square-integration"] as const;
  const languageMutation = useMutation<void, ApiError, StaffLanguage>({
    mutationFn: (nextLanguage) =>
      runEffectMutation(
        Effect.promise(() => setLanguage(nextLanguage)).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.void : Effect.fail(result.error),
          ),
          Effect.withSpan("Settings.updateLanguage"),
        ),
      ),
    onSuccess: () => setNotice(t("settings.languageSaved")),
  });
  const integration = useQuery<SquareIntegration, ApiError>({
    queryKey: integrationQueryKey,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        Effect.gen(function* () {
          const connection = yield* api.requestEffect({
            path: "/square-oauth/status",
            schema: connectionSchema,
            signal,
          });
          const store =
            connection.connected && connection.merchantId
              ? yield* api.requestEffect({
                  path: `/merchants/${encodeURIComponent(connection.merchantId)}/online-store`,
                  schema: storeSchema,
                  signal,
                })
              : undefined;

          return { connection, store } as const;
        }).pipe(Effect.withSpan("Settings.loadSquareIntegration")),
        signal,
      ),
  });
  const callbackMutation = useMutation<
    typeof successSchema.Type,
    ApiError,
    OAuthCallback
  >({
    retry: false,
    mutationFn: ({
      code,
      state,
    }: OAuthCallback) =>
      runEffectMutation(
        api.requestEffect({
          path: "/square-oauth/callback",
          method: "POST",
          body: { code, state },
          schema: successSchema,
        }),
      ),
    onSuccess: () => {
      setNotice("Square connected successfully.");
      void queryClient.invalidateQueries({ queryKey: integrationQueryKey });
    },
  });
  useEffect(() => {
    if (callbackStarted.current) return;
    callbackStarted.current = true;
    const completeCallback = () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      if (params.has("error") || code || state) {
        window.history.replaceState({}, "", window.location.pathname);
        const expectedState = sessionStorage.getItem("squareOAuthState");
        sessionStorage.removeItem("squareOAuthState");
        if (params.has("error") || !code || !state || state !== expectedState)
          setActionError(
            "Square connection was cancelled or could not be verified. Please try connecting again.",
          );
        else callbackMutation.mutate({ code, state });
      }
    };
    completeCallback();
  }, [callbackMutation]);
  const connectMutation = useMutation<
    { readonly target: URL; readonly state: string },
    ApiError,
    void
  >({
    retry: false,
    mutationFn: () =>
      runEffectMutation(
        api
          .requestEffect({
            path: "/square-oauth/authorize",
            method: "POST",
            schema: authorizeSchema,
          })
          .pipe(
            Effect.flatMap((result) =>
              Effect.try({
                try: () => {
                  const target = new URL(result.url);
                  if (
                    target.protocol !== "https:" ||
                    ![
                      "connect.squareup.com",
                      "connect.squareupsandbox.com",
                    ].includes(target.hostname)
                  )
                    throw new Error("Unexpected Square OAuth host");

                  return { target, state: result.state } as const;
                },
                catch: () =>
                  new ApiError(
                    502,
                    "Square returned an unexpected sign-in address.",
                  ),
              }),
            ),
          ),
      ),
    onSuccess: ({ target, state }) => {
      sessionStorage.setItem("squareOAuthState", state);
      window.location.assign(target.href);
    },
  });
  const connect = () => {
    setActionError(undefined);
    setNotice(undefined);
    connectMutation.reset();
    connectMutation.mutate();
  };
  const disconnectMutation = useMutation<
    typeof successSchema.Type,
    ApiError,
    void
  >({
    retry: false,
    mutationFn: () =>
      runEffectMutation(
        api.requestEffect({
          path: "/square-oauth/disconnect",
          method: "POST",
          schema: successSchema,
        }),
      ),
    onSuccess: () => {
      setDisconnecting(false);
      setNotice("Square disconnected.");
      void queryClient.invalidateQueries({ queryKey: integrationQueryKey });
    },
  });
  const disconnect = () => {
    setActionError(undefined);
    setNotice(undefined);
    disconnectMutation.reset();
    disconnectMutation.mutate();
  };
  const storeMutation = useMutation<
    typeof storeSchema.Type,
    ApiError,
    StoreChange
  >({
    retry: false,
    mutationFn: ({
      merchantId,
      status,
    }: StoreChange) =>
      runEffectMutation(
        api.requestEffect({
          path: `/merchants/${encodeURIComponent(merchantId)}/online-store`,
          method: "PUT",
          body: { status },
          schema: storeSchema,
        }),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(
        integrationQueryKey,
        (current: typeof integration.data) =>
          current ? { ...current, store: result } : current,
      );
      setNotice(`Online store ${result.status}.`);
    },
  });
  const toggleStore = () => {
    const connection = integration.data?.connection;
    const store = integration.data?.store?.status;

    if (!connection?.merchantId || !store) return;
    setActionError(undefined);
    setNotice(undefined);
    storeMutation.reset();
    storeMutation.mutate({
      merchantId: connection.merchantId,
      status: store === "open" ? "closed" : "open",
    });
  };
  const pending =
    callbackMutation.isPending ||
    connectMutation.isPending ||
    disconnectMutation.isPending ||
    storeMutation.isPending;
  const mutationError =
    callbackMutation.error?.message ??
    connectMutation.error?.message ??
    disconnectMutation.error?.message ??
    storeMutation.error?.message;
  return (
    <div className="settings-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{t("settings.eyebrow")}</p>
          <h1>{t("settings.title")}</h1>
          <p className="muted">{t("settings.description")}</p>
        </div>
      </header>
      {(actionError ?? mutationError) && (
        <p className="alert" role="alert">
          {actionError ?? mutationError}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      <section className="settings-card">
        <h2>{t("settings.languageTitle")}</h2>
        <p>{t("settings.languageDescription")}</p>
        <label className="field language-setting">
          {t("settings.languageLabel")}
          <select
            value={language}
            disabled={languageMutation.isPending}
            onChange={(event) => {
              const selection = event.currentTarget.value;
              if (selection !== "en" && selection !== "ja") return;
              setActionError(undefined);
              setNotice(undefined);
              languageMutation.mutate(selection);
            }}
          >
            <option value="en">{t("settings.english")}</option>
            <option value="ja">{t("settings.japanese")}</option>
          </select>
        </label>
        {languageMutation.isPending && (
          <p className="muted" role="status">
            {t("settings.languageSaving")}
          </p>
        )}
        {languageMutation.error && (
          <p className="alert" role="alert">
            {languageMutation.error.message}
          </p>
        )}
      </section>
      {api.supportsPasskeys && <StaffAccountSettings />}
      {Match.value(integration).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("settings.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when(
          { status: "success" },
          ({ data: { connection, store: storeResult }, isFetching }) => {
            const store = storeResult?.status;

            return (
              <div aria-busy={isFetching}>
                {connection.merchantId && (
                  <>
                    <PaymentDeviceSettings
                      key={`devices-${connection.merchantId}`}
                      merchantId={connection.merchantId}
                    />
                    <KitchenPresetSettings
                      key={connection.merchantId}
                      merchantId={connection.merchantId}
                    />
                  </>
                )}
                <section className="settings-card">
                  <h2>{t("settings.onlineStore")}</h2>
                  <p>{t("settings.onlineStoreDescription")}</p>
                  <div className="settings-row">
                    <span className="pill">
                      {store ?? t("settings.unavailable")}
                    </span>
                    <button
                      className="primary"
                      disabled={pending || !store}
                      onClick={() => void toggleStore()}
                    >
                      {pending
                        ? t("settings.saving")
                        : store === "open"
                          ? t("settings.closeStore")
                          : t("settings.openStore")}
                    </button>
                  </div>
                </section>
                <section className="settings-card">
                  <h2>{t("settings.square")}</h2>
                  <p>
                    {connection.connected
                      ? t("settings.squareConnected")
                      : t("settings.squareDisconnected")}
                  </p>
                  {connection.merchantId && (
                    <p className="muted">
                      Merchant {connection.merchantId} · {connection.environment}
                    </p>
                  )}
                  <div className="button-row">
                    <button
                      className="primary"
                      disabled={pending}
                      onClick={() => void connect()}
                    >
                      {connection.connected
                        ? t("settings.reconnectSquare")
                        : t("settings.connectSquare")}
                    </button>
                    {connection.connected && (
                      <button
                        className="secondary"
                        disabled={pending}
                        onClick={() => setDisconnecting(true)}
                      >
                        {t("settings.disconnect")}
                      </button>
                    )}
                  </div>
                </section>
              </div>
            );
          },
        ),
        Match.exhaustive,
      )}
      {disconnecting && (
        <StaffDialog
          title={t("settings.disconnectTitle")}
          close={() => setDisconnecting(false)}
        >
          <div className="dialog-body">
            <p>{t("settings.disconnectWarning")}</p>
          </div>
          <footer className="dialog-footer">
            <button
              className="secondary"
              disabled={pending}
              onClick={() => setDisconnecting(false)}
            >
              {t("settings.keepConnected")}
            </button>
            <button
              className="primary danger"
              disabled={pending}
              onClick={() => void disconnect()}
            >
              {t("settings.disconnectSquare")}
            </button>
          </footer>
        </StaffDialog>
      )}
    </div>
  );
}
